/**
 * La moneda de una casilla de dinero cuando la pantalla solo sabe su CÓDIGO
 * (o que es la principal). Lo normal es `useFormatoDeMoneda`, más abajo:
 *
 *     const formatoDe = useFormatoDeMoneda();
 *     <Campo type="number" moneda={formatoDe("VES")} … />   // enseña «= 785,15 Bs»
 *     <Campo type="number" moneda={formatoDe()} … />        // la principal
 *
 * En una moneda sin decimales (COP) el punto es de miles: ver `pasoDe` y
 * `lecturaDeMonto` en money.ts.
 */

import { useQuery } from "@tanstack/react-query";
import { monedas } from "./endpoints";
import { comoFormato, pasoDe, type FormatoMoneda } from "./money";

/**
 * Lo mismo para los campos de dinero que son de TEXTO (con su propia lectura,
 * no `type="number"`): en una moneda sin decimales quita el punto y la coma de
 * lo escrito, antes de guardarlo en el estado.
 *
 *     const limpiar = useLimpiarMonto();
 *     onChange={(e) => setMonto(limpiar(e.target.value))}        // la principal
 *     onChange={(e) => setMonto(limpiar(e.target.value, "COP"))}
 */
export function useLimpiarMonto(): (texto: string, codigo?: string | null) => string {
  const paso = usePasoDeMoneda();
  return (texto, codigo) => (paso(codigo) === "1" ? texto.replace(/[.,]/g, "") : texto);
}

export function usePasoDeMoneda(): (codigo?: string | null) => "1" | "any" {
  const { data } = useQuery({ queryKey: ["monedas-negocio"], queryFn: monedas.listar });
  return (codigo) => {
    const moneda = data?.find((m) => (codigo ? m.currency_code === codigo : m.is_primary));
    return pasoDe(moneda?.decimals);
  };
}

/**
 * El formato de una moneda por su código, para darle `moneda` a una casilla
 * de dinero (`<Campo moneda={formatoDe("VES")} />`): con él enseña cómo
 * entendió lo escrito y deja solo enteros si la moneda no lleva decimales.
 * Sin código, la principal. `null` mientras no han llegado las monedas.
 */
export function useFormatoDeMoneda(): (codigo?: string | null) => FormatoMoneda | null {
  const { data } = useQuery({ queryKey: ["monedas-negocio"], queryFn: monedas.listar });
  return (codigo) => {
    const moneda = data?.find((m) => (codigo ? m.currency_code === codigo : m.is_primary));
    return moneda ? comoFormato(moneda) : null;
  };
}
